<template>
  <div class="org-topology">
    <!-- 左侧面板 -->
    <div class="org-sidebar">
      <div class="sidebar-title">🏢 组织架构</div>

      <div class="sidebar-section">
        <div class="section-label">新增部门</div>
        <el-input v-model="newDeptName" size="mini" placeholder="部门名称" @keyup.enter.native="addRootDept" />
        <el-button size="mini" type="primary" style="margin-top:6px;width:100%" @click="addRootDept">+ 添加部门</el-button>
      </div>

      <div class="sidebar-section">
        <div class="section-label">未分配员工</div>
        <div class="unassigned-list">
          <div v-for="bot in unassignedBots" :key="bot.id" class="bot-chip"
            draggable="true" @dragstart="onBotDragStart($event, bot)">
            <img :src="getAvatarSrc(bot)" class="bot-chip-avatar" />
            <span class="bot-chip-name">{{ bot.nickName }}</span>
          </div>
          <div v-if="unassignedBots.length === 0" class="no-unassigned">全部已分配 ✓</div>
        </div>
      </div>

      <div class="sidebar-tips">
        <div>💡 拖拽员工到部门节点</div>
        <div>🖱 右键部门可操作</div>
        <div>🔄 自动布局排列</div>
      </div>
    </div>

    <!-- 画布 -->
    <div class="org-canvas-wrap" @dragover.prevent @drop="onCanvasDrop">
      <div ref="container" class="org-canvas"></div>
      <div v-if="departments.length === 0" class="canvas-empty">
        <div style="font-size:40px">🏢</div>
        <div>在左侧添加部门，开始构建组织架构</div>
      </div>
    </div>

    <!-- 右键菜单 -->
    <div v-if="ctxMenu.visible" class="ctx-menu" :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }">
      <div class="ctx-item" @click="ctxRename">✏️ 重命名</div>
      <div class="ctx-item" @click="ctxAddChild">➕ 添加子部门</div>
      <div class="ctx-item ctx-danger" @click="ctxDelete">🗑 删除部门</div>
    </div>
  </div>
</template>

<script>
import { Graph, Dom } from '@antv/x6';
import dagre from 'dagre';
import { getOrganization, saveOrganization, checkOrganizationUpdate } from '@/api/organizationApi';

const COLORS = ['#5F95FF','#8b5cf6','#ec4899','#f97316','#22c55e','#14b8a6','#e11d48','#eab308'];
const AVATAR_VARIANTS = [
  { shell: ['#f5f6f8', '#eaedf2', '#d9dee6'], ring: '#c9d0db', plate: '#fbfcfe', plateBorder: '#d7dee8', icon: '#566273', shape: '32%' },
  { shell: ['#f6f5f3', '#eeebe6', '#dcd5cc'], ring: '#d1c8bb', plate: '#fffdfa', plateBorder: '#e3dbcf', icon: '#6a5f53', shape: '42% 24% 42% 24%' },
  { shell: ['#f5f7fa', '#e7edf5', '#d0d8e5'], ring: '#c1cbdb', plate: '#fbfdff', plateBorder: '#d5dfeb', icon: '#546983', shape: '24% 44% 24% 44%' },
  { shell: ['#f7f6f4', '#ece8e1', '#ddd3c7'], ring: '#d2c7b8', plate: '#fffdf9', plateBorder: '#e4dacc', icon: '#6e6150', shape: '36%' },
  { shell: ['#f4f5f7', '#e7eaef', '#d5dce4'], ring: '#c7cfda', plate: '#fafcff', plateBorder: '#d9e0e8', icon: '#58697b', shape: '50% 22% 38% 22%' },
  { shell: ['#f6f7f8', '#eaedf1', '#d6dce3'], ring: '#cdd3dc', plate: '#fcfdfe', plateBorder: '#dbe1e8', icon: '#5a6570', shape: '22% 46% 22% 46%' },
  { shell: ['#f7f6f5', '#eceae7', '#dbd7d1'], ring: '#d0cbc5', plate: '#fdfcfb', plateBorder: '#dfdcd6', icon: '#65605a', shape: '48% 22% 48% 22%' },
  { shell: ['#f5f6f8', '#ebedf1', '#d7dce3'], ring: '#c8d0da', plate: '#fbfcfe', plateBorder: '#d8dee6', icon: '#55606f', shape: '20%' },
];

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) { h = ((h << 5) - h) + str.charCodeAt(i); h |= 0; }
  return Math.abs(h);
}

function svgPathSet(index, color) {
  const common = `stroke="${color}" stroke-width="2.9" stroke-linecap="round" stroke-linejoin="round" fill="none"`;
  const base = `
    <circle cx="32" cy="23" r="13" ${common}/>
    <path d="M16 52c2.5-8 9-13 16-13s13.5 5 16 13" ${common}/>
    <path d="M27 23h.01M37 23h.01" ${common}/>
    <path d="M27 31c3 2.4 7 2.4 10 0" ${common}/>
  `;
  const glyphs = [
    `${base}<path d="M20 16c2-7 7-10 12-10s10 3 12 10" ${common}/><path d="M18 18l-4 6 4 6M46 18l4 6-4 6" ${common}/>`,
    `${base}<rect x="20" y="15" width="24" height="11" rx="5.5" ${common}/><path d="M24 45h16" ${common}/><path d="M32 39v12" ${common}/>`,
    `${base}<path d="M23 14h18" ${common}/><path d="M32 14V7" ${common}/><circle cx="32" cy="5" r="2.3" ${common}/><path d="M17 49h30" ${common}/>`,
    `${base}<path d="M18 19c0-6 5-10 10-10h8c5 0 10 4 10 10" ${common}/><path d="M20 41l6 6M44 41l-6 6" ${common}/>`,
    `${base}<path d="M19 18h26" ${common}/><path d="M23 14l9-6 9 6" ${common}/><path d="M26 41l6 8 6-8" ${common}/>`,
    `${base}<path d="M19 21c3-3 7-5 13-5s10 2 13 5" ${common}/><path d="M21 14l6 5M43 14l-6 5" ${common}/><rect x="26" y="41" width="12" height="9" rx="2.5" ${common}/>`,
    `${base}<path d="M14 24h6M44 24h6" ${common}/><path d="M20 18c2-4 7-7 12-7s10 3 12 7" ${common}/><path d="M23 47c2.5 2 5.5 3 9 3s6.5-1 9-3" ${common}/>`,
    `${base}<rect x="23" y="15" width="18" height="15" rx="4.5" ${common}/><path d="M15 45h9l4 6 4-6h17" ${common}/>`,
  ];
  return glyphs[index % glyphs.length];
}

function createAvatarDataUri(seed, avatarIndex) {
  const resolvedIndex = Number.isFinite(avatarIndex) ? Math.max(0, avatarIndex) : hashCode(seed || 'agent');
  const variant = AVATAR_VARIANTS[resolvedIndex % AVATAR_VARIANTS.length];
  const gradientId = `g${resolvedIndex}`;
  const clipId = `c${resolvedIndex}`;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
      <defs>
        <linearGradient id="${gradientId}" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="${variant.shell[0]}"/>
          <stop offset="0.52" stop-color="${variant.shell[1]}"/>
          <stop offset="1" stop-color="${variant.shell[2]}"/>
        </linearGradient>
        <clipPath id="${clipId}"><rect x="1.5" y="1.5" width="61" height="61" rx="15"/></clipPath>
      </defs>
      <rect x="1.5" y="1.5" width="61" height="61" rx="15" fill="url(#${gradientId})" stroke="${variant.ring}" stroke-width="1.5"/>
      <g clip-path="url(#${clipId})">
        <rect x="7.5" y="7.5" width="49" height="49" rx="12" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.26)"/>
        <rect x="11" y="11" width="42" height="42" rx="10" fill="${variant.plate}" stroke="${variant.plateBorder}"/>
        <g transform="translate(7 7) scale(0.78)">
          ${svgPathSet(resolvedIndex, variant.icon)}
        </g>
      </g>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const STORAGE_KEY = 'agent_org_data_v2';

// 注册部门节点
Graph.registerNode('dept-node', {
  width: 200, height: 56,
  markup: [
    { tagName: 'rect', attrs: { class: 'card' } },
    { tagName: 'text', attrs: { class: 'dept-name' } },
    { tagName: 'g', attrs: { class: 'btn add-child' }, children: [
      { tagName: 'circle', attrs: { class: 'add-child' } },
      { tagName: 'text', attrs: { class: 'add-child' } },
    ]},
  ],
  attrs: {
    '.card': { refWidth: '100%', refHeight: '100%', rx: 10, ry: 10, fill: '#5F95FF', stroke: '#5F95FF', strokeWidth: 1 },
    '.dept-name': { refX: 0.5, refY: 0.5, fill: '#fff', fontSize: 14, fontWeight: 600, textAnchor: 'middle', textVerticalAnchor: 'middle' },
    '.btn.add-child': { refDx: -16, refY: 8, event: 'dept:add-child' },
    '.btn > circle': { r: 9, fill: 'transparent', stroke: '#fff', strokeWidth: 1 },
    '.btn.add-child > text': { fontSize: 18, fontWeight: 800, fill: '#fff', x: -5, y: 6, fontFamily: 'Times New Roman', text: '+' },
  },
}, true);

// 注册员工节点
Graph.registerNode('bot-node', {
  width: 160, height: 72,
  markup: [
    { tagName: 'rect', attrs: { class: 'card' } },
    { tagName: 'image', attrs: { class: 'avatar' } },
    { tagName: 'text', attrs: { class: 'bot-name' } },
    { tagName: 'text', attrs: { class: 'bot-sub' } },
  ],
  attrs: {
    '.card': { refWidth: '100%', refHeight: '100%', rx: 10, ry: 10, fill: '#fff', stroke: '#5F95FF', strokeWidth: 1.5 },
    '.avatar': { x: 12, y: 16, width: 40, height: 40 },
    '.bot-name': { x: 60, y: 26, fill: '#1a1a2e', fontSize: 13, fontWeight: 600 },
    '.bot-sub': { x: 60, y: 46, fill: '#9ca3af', fontSize: 11 },
  },
}, true);

// 注册边
Graph.registerEdge('org-edge', {
  zIndex: -1,
  attrs: { line: { strokeWidth: 2, stroke: '#A2B1C3', sourceMarker: null, targetMarker: null } },
}, true);

export default {
  name: 'AgentOrgTopology',
  props: { bots: { type: Array, default: () => [] } },
  data() {
    return {
      graph: null,
      departments: [],   // { id, name, parentId }
      assignments: {},   // { botId: deptId }
      version: 0,
      hasLoadedFromServer: false,
      hasLocalChanges: false,
      serverHasData: false,
      isSaving: false,
      newDeptName: '',
      ctxMenu: { visible: false, x: 0, y: 0, deptId: null },
      draggingBot: null,
    };
  },
  computed: {
    unassignedBots() {
      const assigned = new Set(Object.keys(this.assignments));
      return this.bots.filter(b => !assigned.has(String(b.id)));
    },
  },
  async mounted() {
    await this.loadData();
    this.$nextTick(() => this.initGraph());
    document.addEventListener('click', this.hideCtxMenu);
    window.addEventListener('focus', this.checkRemoteUpdate);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.hideCtxMenu);
    window.removeEventListener('focus', this.checkRemoteUpdate);
    if (this.graph) this.graph.dispose();
  },
  watch: {
    bots() { this.renderGraph(); },
  },
  methods: {
    normalizeOrgData(data) {
      const departments = Array.isArray(data?.departments) ? data.departments : [];
      const seen = new Set();
      const normalizedDepartments = departments
        .filter(dept => dept && dept.id && !seen.has(dept.id) && seen.add(dept.id))
        .map(dept => ({
          id: dept.id,
          name: (dept.name || '未命名部门').trim() || '未命名部门',
          parentId: dept.parentId || null,
        }));

      const deptIds = new Set(normalizedDepartments.map(dept => dept.id));
      const sanitizedDepartments = normalizedDepartments.map(dept => (
        !dept.parentId || deptIds.has(dept.parentId)
          ? dept
          : { ...dept, parentId: null }
      ));
      const sanitizedDeptIds = new Set(sanitizedDepartments.map(dept => dept.id));
      const assignments = {};
      Object.entries(data?.assignments || {}).forEach(([botId, deptId]) => {
        if (botId && sanitizedDeptIds.has(deptId)) {
          assignments[String(botId)] = deptId;
        }
      });

      return { departments: sanitizedDepartments, assignments };
    },
    isOrgDataEmpty(data) {
      return (data.departments || []).length === 0 && Object.keys(data.assignments || {}).length === 0;
    },
    parseCanvasData(canvasData) {
      if (!canvasData || canvasData === '{}') return { departments: [], assignments: {} };
      return this.normalizeOrgData(JSON.parse(canvasData));
    },
    async loadData() {
      try {
        const response = await getOrganization();
        const data = this.parseCanvasData(response?.canvasData);
        this.departments = data.departments;
        this.assignments = data.assignments;
        this.version = response?.version || 0;
        this.hasLoadedFromServer = true;
        this.hasLocalChanges = false;
        this.serverHasData = !this.isOrgDataEmpty(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.warn('loadData', e);
        this.hasLoadedFromServer = false;
        this.$message?.error?.('组织架构加载失败，请刷新后再操作');
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) {
            const d = this.normalizeOrgData(JSON.parse(raw));
            this.departments = d.departments;
            this.assignments = d.assignments;
          }
        } catch (localError) {
          console.warn('load local org cache failed', localError);
        }
      }
    },
    async saveData() {
      if (!this.hasLoadedFromServer) {
        this.$message?.error?.('组织架构尚未从服务器加载完成，已阻止保存');
        return;
      }
      if (!this.hasLocalChanges || this.isSaving) return;

      const data = this.normalizeOrgData({ departments: this.departments, assignments: this.assignments });
      if (this.serverHasData && this.isOrgDataEmpty(data)) {
        this.$message?.error?.('已阻止空组织架构覆盖服务器已有数据，请刷新后再操作');
        return;
      }
      if (!this.serverHasData && this.isOrgDataEmpty(data)) {
        this.hasLocalChanges = false;
        return;
      }

      this.isSaving = true;
      try {
        const response = await saveOrganization(JSON.stringify(data), this.version);
        this.version = response?.version || this.version + 1;
        this.hasLocalChanges = false;
        this.serverHasData = !this.isOrgDataEmpty(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.warn('save org failed', e);
        this.$message?.error?.('组织架构保存失败，请刷新后重试');
      } finally {
        this.isSaving = false;
      }
    },
    async checkRemoteUpdate() {
      if (!this.hasLoadedFromServer || this.hasLocalChanges || this.isSaving) return;
      try {
        const result = await checkOrganizationUpdate(this.version);
        if (result?.hasUpdate) {
          await this.loadData();
          this.renderGraph();
        }
      } catch (e) {
        console.warn('check org update failed', e);
      }
    },
    markChangedAndSave() {
      this.hasLocalChanges = true;
      this.renderGraph();
      this.saveData();
    },

    initGraph() {
      if (!this.$refs.container) return;
      this.graph = new Graph({
        container: this.$refs.container,
        width: this.$refs.container.offsetWidth,
        height: this.$refs.container.offsetHeight,
        background: { color: '#f7f8fa' },
        grid: { visible: true, size: 20, type: 'dot', args: { color: '#d1d5db' } },
        mousewheel: { enabled: true, modifiers: ['ctrl', 'meta'] },
        panning: { enabled: true },
        interacting: false,  // 禁止拖动节点，布局由 dagre 控制
      });

      // 部门节点上的 + 按钮
      this.graph.on('dept:add-child', ({ node }) => {
        const deptId = node.getData().id;
        this.$prompt('请输入子部门名称', '添加子部门', { confirmButtonText: '确定', cancelButtonText: '取消' })
          .then(({ value }) => {
            if (!value || !value.trim()) return;
            this.departments.push({ id: 'dept-' + Date.now(), name: value.trim(), parentId: deptId });
            this.markChangedAndSave();
          }).catch(() => {});
      });

      // 右键菜单（部门节点）
      this.graph.on('node:contextmenu', ({ node, e }) => {
        if (node.getData()?.type !== 'dept') return;
        e.preventDefault();
        this.ctxMenu = { visible: true, x: e.clientX, y: e.clientY, deptId: node.getData().id };
      });

      this.renderGraph();
    },

    renderGraph() {
      if (!this.graph) return;
      this.graph.clearCells();
      if (this.departments.length === 0) return;

      const cells = [];

      // 部门节点
      this.departments.forEach(dept => {
        const color = COLORS[hashCode(dept.id) % COLORS.length];
        cells.push(this.graph.createNode({
          id: 'dept-' + dept.id,
          shape: 'dept-node',
          attrs: {
            '.card': { fill: color, stroke: color },
            '.dept-name': { text: Dom.breakText(dept.name, { width: 160, height: 40 }) },
          },
          data: { type: 'dept', id: dept.id },
        }));
      });

      // 员工节点
      this.bots.forEach(bot => {
        const deptId = this.assignments[String(bot.id)];
        if (!deptId) return;
        const color = COLORS[hashCode(String(bot.id)) % COLORS.length];
        cells.push(this.graph.createNode({
          id: 'bot-' + bot.id,
          shape: 'bot-node',
          attrs: {
            '.card': { stroke: color },
            '.avatar': { 'xlink:href': this.getAvatarSrc(bot), href: this.getAvatarSrc(bot) },
            '.bot-name': { text: bot.nickName.length > 8 ? bot.nickName.slice(0, 8) + '…' : bot.nickName },
            '.bot-sub': { text: 'Bot · ' + (bot.openclawAgentId || '').slice(0, 8) },
          },
          data: { type: 'bot', id: bot.id, deptId },
        }));
      });

      // 部门层级边
      this.departments.forEach(dept => {
        if (!dept.parentId) return;
        cells.push(this.graph.createEdge({
          shape: 'org-edge',
          source: { cell: 'dept-' + dept.parentId },
          target: { cell: 'dept-' + dept.id },
        }));
      });

      // 员工 → 部门边
      this.bots.forEach(bot => {
        const deptId = this.assignments[String(bot.id)];
        if (!deptId) return;
        cells.push(this.graph.createEdge({
          shape: 'org-edge',
          source: { cell: 'dept-' + deptId },
          target: { cell: 'bot-' + bot.id },
          attrs: { line: { stroke: '#d1d5db', strokeWidth: 1, strokeDasharray: '4 3' } },
        }));
      });

      this.graph.resetCells(cells);
      this.layout();
    },

    layout() {
      const nodes = this.graph.getNodes();
      const edges = this.graph.getEdges();
      const g = new dagre.graphlib.Graph();
      g.setGraph({ rankdir: 'TB', nodesep: 24, ranksep: 40 });
      g.setDefaultEdgeLabel(() => ({}));

      nodes.forEach(node => {
        const size = node.getSize();
        g.setNode(node.id, { width: size.width, height: size.height });
      });
      edges.forEach(edge => {
        const src = edge.getSource();
        const tgt = edge.getTarget();
        if (src.cell && tgt.cell) g.setEdge(src.cell, tgt.cell);
      });

      dagre.layout(g);

      this.graph.freeze();
      g.nodes().forEach(id => {
        const node = this.graph.getCellById(id);
        if (node) { const pos = g.node(id); node.position(pos.x - pos.width / 2, pos.y - pos.height / 2); }
      });
      this.graph.unfreeze();
      this.graph.zoomToFit({ padding: 40 });
    },

    // ─── 部门操作 ─────────────────────────────────────────
    addRootDept() {
      const name = this.newDeptName;
      if (!name || !name.trim()) return;
      this.departments.push({ id: 'dept-' + Date.now(), name: name.trim(), parentId: null });
      this.newDeptName = '';
      this.markChangedAndSave();
    },

    hideCtxMenu() { this.ctxMenu.visible = false; },

    ctxRename() {
      const deptId = this.ctxMenu.deptId;
      const dept = this.departments.find(d => d.id === deptId);
      if (!dept) return;
      this.$prompt('请输入新名称', '重命名部门', { inputValue: dept.name, confirmButtonText: '确定', cancelButtonText: '取消' })
        .then(({ value }) => { if (value && value.trim()) { dept.name = value.trim(); this.markChangedAndSave(); } })
        .catch(() => {});
      this.hideCtxMenu();
    },

    ctxAddChild() {
      const deptId = this.ctxMenu.deptId;
      this.$prompt('请输入子部门名称', '添加子部门', { confirmButtonText: '确定', cancelButtonText: '取消' })
        .then(({ value }) => {
          if (value && value.trim()) {
            this.departments.push({ id: 'dept-' + Date.now(), name: value.trim(), parentId: deptId });
            this.markChangedAndSave();
          }
        }).catch(() => {});
      this.hideCtxMenu();
    },

    ctxDelete() {
      const deptId = this.ctxMenu.deptId;
      this.$confirm('删除部门后，其成员将变为未分配状态', '确认删除', { type: 'warning' })
        .then(() => {
          const toDelete = new Set();
          const collect = id => { toDelete.add(id); this.departments.filter(d => d.parentId === id).forEach(d => collect(d.id)); };
          collect(deptId);
          this.departments = this.departments.filter(d => !toDelete.has(d.id));
          Object.keys(this.assignments).forEach(botId => { if (toDelete.has(this.assignments[botId])) this.$delete(this.assignments, botId); });
          this.markChangedAndSave();
        }).catch(() => {});
      this.hideCtxMenu();
    },

    // ─── 拖拽员工 ─────────────────────────────────────────
    onBotDragStart(e, bot) {
      this.draggingBot = bot;
      e.dataTransfer.setData('botId', String(bot.id));
    },

    onCanvasDrop(e) {
      if (!this.draggingBot || !this.graph) return;
      const p = this.graph.clientToLocal(e.clientX, e.clientY);
      const deptNode = this.graph.getNodes().find(n => {
        if (n.getData()?.type !== 'dept') return false;
        return n.getBBox().containsPoint(p);
      });
      const bot = this.draggingBot;
      this.draggingBot = null;
      if (deptNode) {
        this.$set(this.assignments, String(bot.id), deptNode.getData().id);
        this.markChangedAndSave();
      }
    },

    getAvatarSeed(bot) {
      return String(bot?.openclawAgentId || bot?.id || bot?.nickName || 'agent');
    },
    getAvatarIndex(bot) {
      const seed = this.getAvatarSeed(bot);
      const sorted = [...(this.bots || [])].sort((left, right) =>
        this.getAvatarSeed(left).localeCompare(this.getAvatarSeed(right))
      );
      const index = sorted.findIndex(item => this.getAvatarSeed(item) === seed);
      return index >= 0 ? index : hashCode(seed);
    },
    getAvatarSrc(bot) {
      const image = bot?.headImage && String(bot.headImage).trim();
      if (image) return image;
      return createAvatarDataUri(this.getAvatarSeed(bot), this.getAvatarIndex(bot));
    },
  },
};
</script>

<style lang="scss" scoped>
.org-topology { display: flex; height: 100%; position: relative; overflow: hidden; }

.org-sidebar {
  width: 180px; flex-shrink: 0; background: #fff;
  border-right: 1px solid #e5e7eb;
  display: flex; flex-direction: column; padding: 12px; overflow-y: auto; gap: 12px;
}
.sidebar-title { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.sidebar-section { display: flex; flex-direction: column; gap: 4px; }
.section-label { font-size: 11px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; }
.unassigned-list { display: flex; flex-direction: column; gap: 4px; max-height: 300px; overflow-y: auto; }
.bot-chip {
  display: flex; align-items: center; gap: 6px; padding: 5px 8px;
  background: #f3f4f6; border-radius: 8px; cursor: grab; font-size: 12px;
  border: 1px solid #e5e7eb; transition: background 0.15s;
  &:hover { background: #e0e7ff; border-color: #a5b4fc; }
}
.bot-chip-avatar { width: 22px; height: 22px; border-radius: 8px; object-fit: cover; flex-shrink: 0; }
.bot-chip-name { color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.no-unassigned { font-size: 11px; color: #9ca3af; text-align: center; padding: 8px 0; }
.sidebar-tips { margin-top: auto; font-size: 11px; color: #9ca3af; line-height: 1.8; }

.org-canvas-wrap { flex: 1; position: relative; overflow: hidden; }
.org-canvas { width: 100%; height: 100%; }
.canvas-empty {
  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
  text-align: center; color: #9ca3af; font-size: 13px; pointer-events: none;
}

.ctx-menu {
  position: fixed; background: #fff; border: 1px solid #e5e7eb;
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 9999; min-width: 140px; overflow: hidden;
}
.ctx-item { padding: 9px 14px; font-size: 13px; color: #374151; cursor: pointer; &:hover { background: #f3f4f6; } }
.ctx-danger { color: #ef4444; &:hover { background: #fef2f2; } }
</style>
